<script setup lang="ts">
  import type { DropdownOption, DropdownProps } from 'naive-ui'

  import { useScreenSize } from '@/shared/lib'
  import { getImage } from '@/shared/lib/media'
  import { SignoutPopup } from '@/shared/ui/components'

  import { useProfile, UserAvatar } from '@/entities/profile'

  import { type AvailableLocale, type LocaleItem, LOCALE_ITEMS, useUplatformLocale } from '@/features/locale'

  import UserMenuLangItem from './UserMenuLangItem.vue'

  type MenuKey = 'profile' | 'language' | 'signout' | `lang:${AvailableLocale}`

  const { profile } = storeToRefs(useProfile())
  const { t } = useI18n()
  const { setLocale, currentLocale } = useUplatformLocale()

  const userName = computed(() => profile.value?.firstName || '')
  const showSignoutPopup = ref(false)
  const toggleSignoutPopup = () => (showSignoutPopup.value = !showSignoutPopup.value)

  const menuIsActive = ref(false)
  const { isMobile } = storeToRefs(useScreenSize())

  const userAvatarSize = 40
  const userAvatar = computed(() => getImage({ id: profile.value?.attachments?.[0], quality: 70 }) || '')

  const router = useRouter()

  const dropdownThemeOverrides: NonNullable<DropdownProps['themeOverrides']> = {
    optionTextColor: 'var(--color-body)',
    prefixColor: 'var(--color-body)',
    suffixColor: 'var(--color-body)',
    padding: '8px 4px',
  }

  const renderLangOption = (locale: LocaleItem) => {
    return h(UserMenuLangItem, {
      value: locale.value,
      name: locale.name,
      icon: locale.icon,
      alt: locale.alt,
      isActive: locale.value === currentLocale.value,
    })
  }

  const dropdownOptionProps = { class: 'font-medium' }

  const options = computed<DropdownOption[]>(() => [
    {
      key: 'profile',
      label: t('header_dropdown-profile'),
      icon: () => h('i', { class: 'fa-regular fa-user' }),
      props: dropdownOptionProps,
    },
    {
      key: 'language',
      label: t('header_dropdown-language'),
      icon: () => h('i', { class: 'fa-regular fa-language' }),
      props: dropdownOptionProps,
      children: LOCALE_ITEMS.map((locale) => ({
        key: `lang:${locale.value}`,
        label: () => renderLangOption(locale),
      })),
    },
    {
      type: 'divider',
      key: 'd1',
    },
    {
      key: 'signout',
      label: t('base_signout'),
      icon: () => h('i', { class: 'fa-regular fa-left-from-bracket' }),
      props: dropdownOptionProps,
    },
  ])

  const handleSelect = (key: MenuKey) => {
    if (key === 'profile') {
      router.push(`/profile/${profile.value?.id}`)
    } else if (key.startsWith('lang:')) {
      setLocale(key.slice(5) as AvailableLocale)
    } else if (key === 'signout') {
      toggleSignoutPopup()
    }

    menuIsActive.value = false
  }
</script>

<template>
  <div class="flex items-center">
    <SignoutPopup v-model="showSignoutPopup" />

    <NDropdown
      v-model:show="menuIsActive"
      :options="options"
      placement="bottom-end"
      trigger="click"
      :show-arrow="false"
      :theme-overrides="dropdownThemeOverrides"
      @select="handleSelect"
    >
      <button
        class="relative flex cursor-pointer items-center gap-3 rounded-[20px] border-none bg-transparent text-inherit max-md:pr-0"
        type="button"
        aria-haspopup="menu"
        :aria-expanded="menuIsActive"
      >
        <span
          v-if="!isMobile"
          class="text-invert! body-md-semibold opacity-90"
        >
          {{ userName }}
        </span>
        <UserAvatar
          :src="userAvatar"
          :size="userAvatarSize"
        />
      </button>
    </NDropdown>
  </div>
</template>
